<template>
  <div class="case-editor space-y-6 pb-12 animate-[fadeIn_0.6s_ease-out]">
    <!-- 顶部返回栏 -->
    <div class="flex items-center gap-4">
      <button
        @click="goBack"
        class="flex items-center gap-1 text-sm text-slate-500 hover:text-[#F2C94C] transition-colors"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        返回案例列表
      </button>
      <h3 class="text-base font-black text-slate-900">{{ isEdit ? '编辑案例' : '新增案例' }}</h3>
    </div>

    <!-- 区块 1：基础信息 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-4">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">基础信息</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-5"></div>

      <el-form :model="form" label-width="90px" size="small">
        <el-form-item label="案例标题" required>
          <el-input v-model="form.title" placeholder="请输入案例标题" />
        </el-form-item>
        <el-form-item label="所属专家" required>
          <el-select v-model="form.expertId" placeholder="请选择所属专家" style="width: 100%" @change="handleExpertChange">
            <el-option
              v-for="e in EXPERTS"
              :key="e.id"
              :label="e.name"
              :value="e.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="摘要" required>
          <el-input
            v-model="form.summary"
            type="textarea"
            :rows="2"
            placeholder="一句话描述案例核心内容..."
          />
        </el-form-item>
        <el-form-item label="标签">
          <el-select
            v-model="form.tags"
            multiple
            filterable
            allow-create
            placeholder="请选择或输入标签"
            style="width: 100%"
          >
            <el-option
              v-for="tag in allTags"
              :key="tag"
              :label="tag"
              :value="tag"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="发布状态">
          <el-radio-group v-model="form.publishStatus">
            <el-radio-button label="draft">草稿</el-radio-button>
            <el-radio-button label="published">已发布</el-radio-button>
            <el-radio-button label="offline">已下线</el-radio-button>
          </el-radio-group>
        </el-form-item>
      </el-form>
    </div>

    <!-- 区块 2：内容编辑 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-4">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">内容编辑</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-5"></div>

      <!-- 完整内容 -->
      <div class="mb-6">
        <div class="flex items-center justify-between mb-2">
          <label class="text-sm font-medium text-slate-700">完整内容</label>
          <span class="text-xs text-slate-400">支持 Markdown 格式</span>
        </div>
        <div class="flex gap-4" style="height: 400px;">
          <el-input
            v-model="form.content"
            type="textarea"
            placeholder="输入 Markdown 内容..."
            class="flex-1"
            :style="{ height: '100%' }"
            :input-style="{ height: '100%', resize: 'none' }"
          />
          <div class="flex-1 border border-slate-200 rounded-lg p-4 overflow-y-auto bg-slate-50" v-html="renderMarkdown(form.content)"></div>
        </div>
      </div>

      <!-- 案例背景 -->
      <div class="mb-6">
        <label class="text-sm font-medium text-slate-700 mb-2 block">案例背景</label>
        <el-input
          v-model="form.background"
          type="textarea"
          :rows="3"
          placeholder="描述案例发生的背景和情境..."
        />
      </div>

      <!-- 挑战/难度 -->
      <div class="mb-6">
        <label class="text-sm font-medium text-slate-700 mb-2 block">挑战 / 难度</label>
        <el-input
          v-model="form.difficulty"
          type="textarea"
          :rows="3"
          placeholder="描述面临的核心挑战和难度..."
        />
      </div>

      <!-- 专家洞察 -->
      <div>
        <label class="text-sm font-medium text-slate-700 mb-2 block">专家洞察</label>
        <el-input
          v-model="form.expertInsight"
          type="textarea"
          :rows="4"
          placeholder="专家的核心观点和洞察，前台会高亮展示..."
        />
      </div>
    </div>

    <!-- 区块 3：媒体资源 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-4">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">媒体资源</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-5"></div>

      <!-- 视频资源 -->
      <div class="mb-6">
        <label class="text-sm font-medium text-slate-700 mb-2 block">视频资源</label>
        <div class="flex items-center gap-3 mb-2">
          <input
            ref="videoFileInput"
            type="file"
            accept="video/*"
            class="hidden"
            @change="handleVideoFileSelect"
          />
          <button
            @click="$refs.videoFileInput.click()"
            class="px-3 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50 transition-colors"
          >
            选择本地文件
          </button>
          <span class="text-xs text-slate-400">或</span>
          <el-input v-model="form.videoUrl" placeholder="粘贴外部链接..." size="mini" style="width: 300px" />
        </div>
        <!-- 视频预览 -->
        <div v-if="videoPreviewUrl || form.videoUrl" class="border border-slate-200 rounded-lg overflow-hidden bg-slate-900 max-w-md">
          <video
            v-if="videoPreviewUrl || isValidUrl(form.videoUrl)"
            :src="videoPreviewUrl || form.videoUrl"
            controls
            class="w-full"
            style="max-height: 200px;"
          ></video>
          <div v-else class="p-4 text-center text-slate-400 text-sm">
            无效的 URL
          </div>
        </div>
        <p v-if="videoFileName" class="text-xs text-amber-600 mt-1">
          已选择：{{ videoFileName }}（当前为前端演示环境，文件未实际上传）
        </p>
      </div>

      <!-- 音频资源 -->
      <div>
        <label class="text-sm font-medium text-slate-700 mb-2 block">音频资源</label>
        <div class="flex items-center gap-3 mb-2">
          <input
            ref="audioFileInput"
            type="file"
            accept="audio/*"
            class="hidden"
            @change="handleAudioFileSelect"
          />
          <button
            @click="$refs.audioFileInput.click()"
            class="px-3 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50 transition-colors"
          >
            选择本地文件
          </button>
          <span class="text-xs text-slate-400">或</span>
          <el-input v-model="form.audioUrl" placeholder="粘贴外部链接..." size="mini" style="width: 300px" />
        </div>
        <!-- 音频预览 -->
        <div v-if="audioPreviewUrl || form.audioUrl" class="border border-slate-200 rounded-lg p-3 bg-slate-50 max-w-md">
          <audio
            v-if="audioPreviewUrl || isValidUrl(form.audioUrl)"
            :src="audioPreviewUrl || form.audioUrl"
            controls
            class="w-full"
          ></audio>
          <div v-else class="text-center text-slate-400 text-sm">
            无效的 URL
          </div>
        </div>
        <p v-if="audioFileName" class="text-xs text-amber-600 mt-1">
          已选择：{{ audioFileName }}（当前为前端演示环境，文件未实际上传）
        </p>
      </div>
    </div>

    <!-- 区块 4：关联配置 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-6 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <div class="flex items-center gap-2 mb-4">
        <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        <h4 class="text-sm font-black text-slate-900">关联配置</h4>
      </div>
      <div class="w-12 h-0.5 bg-[#F2C94C] mb-5"></div>

      <el-form :model="form" label-width="90px" size="small">
        <el-form-item label="关联话题">
          <el-select v-model="form.relatedTopicIds" multiple placeholder="请选择关联话题" style="width: 100%">
            <el-option
              v-for="t in TOPICS"
              :key="t.id"
              :label="t.title"
              :value="t.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
    </div>

    <!-- 底部操作栏 -->
    <div class="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-3 flex items-center justify-end gap-3 z-50" style="padding-left: 236px;">
      <button
        @click="goBack"
        class="px-5 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors"
      >
        取消并返回
      </button>
      <button
        @click="handleSave('draft')"
        class="px-5 py-2 border border-[#F2C94C] text-[#F2C94C] text-sm font-medium rounded-full hover:bg-[#F2C94C]/5 transition-colors"
      >
        保存为草稿
      </button>
      <button
        @click="handleSave('published')"
        class="px-5 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
      >
        保存并发布
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { logAction } from '../../services/audit'
import { EXPERT_CASES } from '../../data/cases'
import { EXPERTS, TOPICS } from '../../data'
import { PERMISSIONS } from '../../data/adminConfig'
import { marked } from 'marked'

const DEFAULT_FORM = {
  id: '',
  title: '',
  summary: '',
  content: '',
  background: '',
  difficulty: '',
  expertInsight: '',
  tags: [],
  expertId: '',
  expertName: '',
  videoUrl: '',
  audioUrl: '',
  relatedTopicIds: [],
  publishStatus: 'draft',
  createdAt: null,
}

export default {
  name: 'CaseEditor',

  data() {
    return {
      form: { ...DEFAULT_FORM },
      videoPreviewUrl: '',
      audioPreviewUrl: '',
      videoFileName: '',
      audioFileName: '',
      EXPERTS,
      TOPICS,
      PERMISSIONS,
    }
  },

  computed: {
    ...mapState(['cases']),
    ...mapGetters(['hasPermission', 'currentAdminId']),

    isEdit() {
      return !!this.$route.query.id
    },

    caseId() {
      return this.$route.query.id
    },

    allTags() {
      const tags = new Set()
      const dict = this.cases || { ...EXPERT_CASES }
      Object.values(dict).forEach(c => {
        if (c.tags) {
          c.tags.forEach(tag => tags.add(tag))
        }
      })
      return Array.from(tags)
    },
  },

  created() {
    if (this.isEdit) {
      this.loadCase()
    } else {
      this.form = { ...DEFAULT_FORM, id: `case-${Date.now()}` }
    }
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    loadCase() {
      const dict = this.cases || { ...EXPERT_CASES }
      const caseData = dict[this.caseId]
      if (!caseData) {
        this.$message.error('案例不存在')
        this.goBack()
        return
      }
      this.form = {
        ...DEFAULT_FORM,
        ...JSON.parse(JSON.stringify(caseData)),
      }
    },

    handleExpertChange(expertId) {
      const expert = EXPERTS.find(e => e.id === expertId)
      if (expert) {
        this.form.expertName = expert.name
      }
    },

    renderMarkdown(text) {
      if (!text) return '<p class="text-slate-400 text-sm">预览区域</p>'
      try {
        return marked(text)
      } catch {
        return '<p class="text-red-400 text-sm">Markdown 渲染错误</p>'
      }
    },

    isValidUrl(url) {
      if (!url) return false
      try {
        new URL(url)
        return true
      } catch {
        return false
      }
    },

    handleVideoFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return
      this.videoFileName = file.name
      const reader = new FileReader()
      reader.onload = (e) => {
        this.videoPreviewUrl = e.target.result
      }
      reader.readAsDataURL(file)
      event.target.value = ''
    },

    handleAudioFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return
      this.audioFileName = file.name
      const reader = new FileReader()
      reader.onload = (e) => {
        this.audioPreviewUrl = e.target.result
      }
      reader.readAsDataURL(file)
      event.target.value = ''
    },

    goBack() {
      this.$router.push('/admin/cases')
    },

    handleSave(forceStatus) {
      if (!this.form.title.trim()) {
        this.$message.error('案例标题不能为空')
        return
      }
      if (!this.form.expertId) {
        this.$message.error('必须选择所属专家')
        return
      }
      if (!this.form.summary.trim()) {
        this.$message.error('摘要不能为空')
        return
      }

      const dict = { ...(this.cases || { ...EXPERT_CASES }) }
      const savedForm = {
        ...this.form,
        title: this.form.title.trim(),
        summary: this.form.summary.trim(),
        publishStatus: forceStatus || this.form.publishStatus,
      }

      if (!this.isEdit) {
        savedForm.createdAt = Date.now()
        savedForm.createdBy = this.currentAdminId
      }

      // 如果选择了本地文件但未填写 URL，使用本地预览 URL（演示环境）
      if (this.videoPreviewUrl && !savedForm.videoUrl) {
        savedForm.videoUrl = this.videoPreviewUrl
      }
      if (this.audioPreviewUrl && !savedForm.audioUrl) {
        savedForm.audioUrl = this.audioPreviewUrl
      }

      dict[savedForm.id] = savedForm
      this.$store.commit('SET_CASES', dict)

      logAction({
        action: this.isEdit ? 'UPDATE' : 'CREATE',
        targetType: 'case',
        targetId: savedForm.id,
        reason: this.isEdit ? `编辑案例: ${savedForm.title}` : `新建案例: ${savedForm.title}`,
        source: 'admin-ui',
        actor: { role: this.$store.state.userRole },
      })

      this.$message.success(this.isEdit ? '保存成功' : '创建成功')
      this.goBack()
    },
  },
}
</script>

<style scoped>
.case-editor {
  padding-bottom: 80px;
}
</style>
